import {
  Box,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  Pagination,
  Chip,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listUsers, ApiUser, banUser, unbanUser } from "../model/users";
import { useAppDispatch, useAppSelector } from "../store";
import { setLastError } from "../store/session";
import AlertDialog from "../components/AlertDialog";

const PAGE_SIZE = 10;

export const ActiveUsers = () => {
  const user = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    userId: string | null;
    action: "ban" | "unban" | null;
    userName: string | null;
  }>({
    open: false,
    userId: null,
    action: null,
    userName: null,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["users", page],
    queryFn: async () => {
      const offset = (page - 1) * PAGE_SIZE;
      const result = await listUsers({ limit: PAGE_SIZE, offset });
      if (result.error) {
        dispatch(setLastError(result.error));
        throw new Error(result.error);
      }
      return result.data;
    },
    enabled: !!user,
  });

  const banUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const result = await banUser(userId);
      if (result.error) {
        throw new Error(result.error);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setSnackbar({
        open: true,
        message: "User banned successfully",
        severity: "success",
      });
      setConfirmationDialog({ open: false, userId: null, action: null, userName: null });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message || "Failed to ban user",
        severity: "error",
      });
      setConfirmationDialog({ open: false, userId: null, action: null, userName: null });
    },
  });

  const unbanUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const result = await unbanUser(userId);
      if (result.error) {
        throw new Error(result.error);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setSnackbar({
        open: true,
        message: "User unbanned successfully",
        severity: "success",
      });
      setConfirmationDialog({ open: false, userId: null, action: null, userName: null });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message || "Failed to unban user",
        severity: "error",
      });
      setConfirmationDialog({ open: false, userId: null, action: null, userName: null });
    },
  });

  // Handle error display
  useEffect(() => {
    if (error) {
      dispatch(setLastError(error instanceof Error ? error.message : String(error)));
    }
  }, [error, dispatch]);

  const formatUserDisplayName = (user: ApiUser): string => {
    return user.user_name || user.email || "Unknown user";
  };

  const formatCreatedAt = (user: ApiUser): string => {
    if (user.created_at) {
      return new Date(user.created_at).toLocaleString();
    }
    return "Unknown";
  };

  const handleBanClick = (apiUser: ApiUser) => {
    setConfirmationDialog({
      open: true,
      userId: apiUser.id,
      action: "ban",
      userName: formatUserDisplayName(apiUser),
    });
  };

  const handleUnbanClick = (apiUser: ApiUser) => {
    setConfirmationDialog({
      open: true,
      userId: apiUser.id,
      action: "unban",
      userName: formatUserDisplayName(apiUser),
    });
  };

  const handleConfirmAction = () => {
    if (confirmationDialog.userId && confirmationDialog.action) {
      if (confirmationDialog.action === "ban") {
        banUserMutation.mutate(confirmationDialog.userId);
      } else {
        unbanUserMutation.mutate(confirmationDialog.userId);
      }
    }
  };

  const handleCloseConfirmation = () => {
    setConfirmationDialog({ open: false, userId: null, action: null, userName: null });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  if (isLoading) {
    return (
      <Box sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!data) {
    return <></>;
  }

  const totalPages = Math.ceil(data.totalCount / PAGE_SIZE);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const isActionLoading = banUserMutation.isPending || unbanUserMutation.isPending;

  return (
    <Stack spacing={2}>
      <Typography variant="h6">
        Users ({data.totalCount} total)
      </Typography>
      {data.data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No users found
        </Typography>
      ) : (
        <>
          {data.data.map((apiUser) => {
            const isBanned = apiUser.banned === true;
            const isUserActionLoading = isActionLoading && confirmationDialog.userId === apiUser.id;
            
            return (
              <Paper elevation={1} style={{ padding: 16 }} key={apiUser.id}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {formatUserDisplayName(apiUser)}
                      </Typography>
                      <Chip
                        label={isBanned ? "Banned" : "Active"}
                        color={isBanned ? "error" : "success"}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {apiUser.email}
                    </Typography>
                    <Typography variant="caption" style={{ opacity: 0.7 }}>
                      Created: {formatCreatedAt(apiUser)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {isUserActionLoading ? (
                      <CircularProgress size={24} />
                    ) : isBanned ? (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleUnbanClick(apiUser)}
                        disabled={isActionLoading}
                      >
                        Unban
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleBanClick(apiUser)}
                        disabled={isActionLoading}
                      >
                        Ban
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
      
      {confirmationDialog.open && confirmationDialog.action && (
        <AlertDialog
          title={confirmationDialog.action === "ban" ? "Ban User" : "Unban User"}
          content={
            confirmationDialog.action === "ban"
              ? `Are you sure you want to ban ${confirmationDialog.userName}?`
              : `Are you sure you want to unban ${confirmationDialog.userName}?`
          }
          close="Cancel"
          ok={confirmationDialog.action === "ban" ? "Ban" : "Unban"}
          onClose={handleCloseConfirmation}
          onOk={handleConfirmAction}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};
